import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { getHunterVehicle, getService, getSpgPricing, IAppointment, IService, ISpgPricingData, LocalizedService, useLocalizedServiceFactory } from '../../../API/GoMoto';
import { HunterServiceStatusType } from '../../../API/Enums';
// This does not provide TypeScript support, but it is better than importing each image.
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../../context/Appointment/appointment';
import { InitialStateType, Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { GMCapitalize } from '../../../controls/Case';
import { CTAButton, GMUpsellButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { HunterServiceStatus } from '../../../controls/Hunter/HunterServiceStatus/HunterServiceStatus';
import { HunterTireCard } from '../../../controls/Hunter/HunterTireCard/HunterTireCard';
import { HunterTireCardGrid } from '../../../controls/Hunter/HunterTireCardGrid/HunterTireCardGrid';
import { ImageHelper } from '../../../controls/ImageHelper/ImageHelper';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { getUpdateAptServices } from '../../../helpers/Appointment';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { DisplayMake, DisplayModel } from '../../../helpers/displayVehicle';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { QDVehicle, useQDVehicleFactory } from '../QDVehicle';
import { default as css } from '../QuickDrive.module.scss';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { AsyncFunction, QuickDriveProps } from './QuickDrive.types';

export const QuickDrive = memo(({
    onProceed,
    onSkip,
}: QuickDriveProps) => {
    useHideKioskBackground()();
    const localizedServiceFactory = useLocalizedServiceFactory();
    const Localization = useLocalization();
    const {
        key,
        dealer,
        dealer: {
            hunterAlignmentEnabled,
            hunterAlignmentOpCode,
            hunterTireTreadEnabled,
            hunterTireTreadOpCode,
        },
    } = useKioskState();
    const appointment: IAppointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const cart: InitialStateType = useCart();
    const cartDispatch = useCartDispatch();
    const [alignmentService, setAlignmentService] = useState<IService>();
    const [treadService, setTreadService] = useState<IService>();

    const alignmentSelected: boolean = cart.services.findIndex((s) => s.opCode === hunterAlignmentOpCode) > -1;
    const treadSelected: boolean = cart.services.findIndex((s) => s.opCode === hunterTireTreadOpCode) > -1;

    const qdVehicleFactory = useQDVehicleFactory();

    const [qdVehicle, setQDVehicle] = useState<QDVehicle>();
    const [loading, setLoading] = useState<boolean>(false);
    const [servicesLoading, setServicesLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!appointment.vin || (!hunterAlignmentEnabled && !hunterTireTreadEnabled)) {
            onSkip();
        }
        else if (!qdVehicle && !loading) {
            setLoading(true);
            getHunterVehicle(dealer.key, appointment.vin)
                .then((vehicle) => {
                    if (vehicle) {
                        setQDVehicle(qdVehicleFactory(vehicle));
                        setLoading(false);
                    }
                    else {
                        onSkip();
                    }
                });
        }
    }, [appointment.vin, onSkip, dealer.key, qdVehicleFactory, qdVehicle, loading, hunterAlignmentEnabled, hunterTireTreadEnabled]);

    useEffect(() => {
        (async () => {
            if (!servicesLoading && qdVehicle) {
                const pullAlign: boolean =
                    !alignmentService &&
                    hunterAlignmentEnabled &&
                    !!hunterAlignmentOpCode &&
                    qdVehicle.alignment.hunterServiceStatusType !== HunterServiceStatusType.OK &&
                    qdVehicle.alignment.hunterServiceStatusType !== HunterServiceStatusType.NoData;
                const pullTire: boolean =
                    !treadService &&
                    hunterTireTreadEnabled &&
                    !!hunterTireTreadOpCode &&
                    qdVehicle.tires.hunterServiceStatusType !== HunterServiceStatusType.OK &&
                    qdVehicle.tires.hunterServiceStatusType !== HunterServiceStatusType.NoData;

                const alignment: Promise<IService> | undefined = (pullAlign && getService(key, hunterAlignmentOpCode)) || undefined;
                const tireTread: Promise<IService> | undefined = (pullTire && getService(key, hunterTireTreadOpCode)) || undefined;

                if (pullAlign || pullTire) {
                    setServicesLoading(true);
                    Promise.all([alignment, tireTread])
                        .then(([alignment, tireTread]) => {
                            (async () => {
                                if (alignment === undefined && tireTread === undefined) {
                                    onSkip();
                                }
                                else {
                                    const spgPricing = await getSpgPricing(
                                        key,
                                        dealer.key,
                                        [alignment?.opCode || '', tireTread?.opCode || ''],
                                        appointment.vin,
                                        parseInt(appointment.year ?? '0', 10) || 0,
                                        DisplayMake(appointment),
                                        DisplayModel(appointment),
                                        appointment.mileage,
                                    );

                                    if (pullAlign && alignment?.useSpgPricing && !!alignment.spgPricing) {
                                        setAlignmentService({
                                            ...alignment,
                                            ...spgPricing?.filter((s: ISpgPricingData) => !!s.isSpgOp && s.opCode === alignment?.opCode)?.[0],
                                        });
                                    }
                                    else {
                                        alignment && setAlignmentService(alignment);
                                    }

                                    if (pullTire && tireTread?.useSpgPricing && !!tireTread.spgPricing) {
                                        setTreadService({
                                            ...tireTread,
                                            ...spgPricing?.filter((s: ISpgPricingData) => !!s.isSpgOp && s.opCode === tireTread?.opCode)?.[0],
                                        });
                                    }
                                    else {
                                        tireTread && setTreadService(tireTread);
                                    }

                                    setServicesLoading(false);
                                }
                            })();
                        });
                }
            }
        })();
    }, [
        alignmentService,
        hunterAlignmentEnabled,
        hunterAlignmentOpCode,
        hunterTireTreadEnabled,
        hunterTireTreadOpCode,
        key,
        dealer.key,
        localizedServiceFactory,
        servicesLoading,
        treadService,
        appointment,
        qdVehicle,
        onSkip,
    ]);

    const alignmentLocalized: LocalizedService | undefined = alignmentService && localizedServiceFactory(alignmentService);
    const treadLocalized: LocalizedService | undefined = treadService && localizedServiceFactory(treadService);

    const handleOnProceed: AsyncFunction = async () => {
        const servicesRecommended: number[] = appointment.servicesRecommended || [];

        if (hunterAlignmentEnabled && qdVehicle?.alignment.recommended && alignmentLocalized && !servicesRecommended.includes(alignmentLocalized.id)) {
            servicesRecommended.push(alignmentLocalized.id);
        }

        if (hunterTireTreadEnabled && qdVehicle?.tires.recommended && treadLocalized && !servicesRecommended.includes(treadLocalized.id)) {
            servicesRecommended.push(treadLocalized.id);
        }

        await setAppointment({
            ...appointment,
            servicesRecommended,
            servicesConfirmed: getUpdateAptServices(cart.services),
            isHunter: true,
        });
        onProceed();
    };

    return !loading && !servicesLoading && qdVehicle && appointment && appointment.vin && (hunterAlignmentEnabled || hunterTireTreadEnabled) ? (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={
                        <GMAppointmentName />
                    }
                    secondaryHeading={Localization.QuickDriveScan}
                    leftAlign
                />}
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            type={'submit'}
                            styleType={'primary'}
                            onClick={handleOnProceed}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />,
                    ]}
                />
            }
            topDivider
        >
            <div className={css.quickDrive}>
                <div>
                    <div className={css.vehicleDetails}>
                        <div className={css.imageWrapper}>
                            <ImageHelper
                                imageProps={{
                                    src: qdVehicle.vehicleImage,
                                    alt: Localization.HunterAlignmentImage,
                                }}
                                fullyQualified
                            />
                        </div>
                        <div className={css.details}>
                            <h4 className={css.detail}>
                                <span className={css.label}>{Localization.Year}{': '}</span>
                                <span>{appointment.year || ''}</span>
                            </h4>
                            <h4 className={css.detail}>
                                <span className={css.label}>{Localization.Make}{': '}</span>
                                <GMCapitalize>{DisplayMake(appointment)}</GMCapitalize>
                            </h4>
                            <h4 className={css.detail}>
                                <span className={css.label}>{Localization.Model}{': '}</span>
                                <GMCapitalize>{DisplayModel(appointment)}</GMCapitalize>
                            </h4>
                            <h4 className={css.detail}>
                                <span className={css.label}>{Localization.VIN}{': '}</span>
                                <span>{appointment.vin}</span>
                            </h4>
                        </div>
                    </div>
                </div>
                <Spacer />
                <div className={classNames({ [css.alignmentTread]: hunterAlignmentEnabled && hunterTireTreadEnabled })}>
                    {hunterAlignmentEnabled && (
                        <div>
                            <HunterServiceStatus label={qdVehicle.alignment.text.short(Localization)} status={qdVehicle.alignment.hunterServiceStatusType} />
                            <div className={css.iconDescriptionBox}>
                                <div className={css.imageWrapper}>
                                    <ImageHelper
                                        imageProps={{
                                            src: qdVehicle.alignment.iconImg,
                                            alt: qdVehicle.alignment.recommended
                                                ? Localization.AlignmentFailedInspectionIcon
                                                : Localization.AlignmentPassedInspectionIcon,
                                        }}
                                    />
                                </div>
                                <h5 className={css.description}>{qdVehicle.alignment.text.long(Localization)}</h5>
                            </div>

                            <div className={css.graphicWrapper}>{qdVehicle.drawAlignmentGraphic()}</div>
                            {alignmentService &&
                                alignmentLocalized &&
                                qdVehicle.alignment.hunterServiceStatusType !== HunterServiceStatusType.OK &&
                                qdVehicle.alignment.hunterServiceStatusType !== HunterServiceStatusType.NoData ?
                                (
                                    <GMUpsellButton
                                        title={alignmentSelected ? undefined : Localization.ClickToAddThisService}
                                        label={alignmentSelected
                                            ? Localization.formatString(Localization.RemoveGenericFormat, alignmentLocalized.localization.name)
                                                .toString()
                                            : Localization.formatString(Localization.AddGenericFormat, alignmentLocalized.localization.name)
                                                .toString()}
                                        styleType={alignmentSelected ? 'primary' : 'secondary'}
                                        onClick={() => {
                                            alignmentService && cartDispatch({
                                                type: Types.ToggleService,
                                                payload: alignmentService,
                                            });
                                        }}
                                        add={!alignmentSelected}
                                        price={(
                                            <SpgPriceText
                                                price={alignmentLocalized.price}
                                                priceLow={alignmentLocalized.priceLow}
                                                priceHigh={alignmentLocalized.priceHigh}
                                                spgPricing={alignmentLocalized.spgPricing}
                                                useSpgPricingLogic={!!alignmentLocalized.useSpgPricing}
                                            />
                                        )}
                                        id={alignmentSelected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                                        ariaLabel={alignmentSelected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                                    />
                                ) : null}
                        </div>
                    )}
                    {hunterAlignmentEnabled && hunterTireTreadEnabled && (isElectron() ? null : <Spacer />)}
                    {hunterTireTreadEnabled && (
                        <div>
                            <HunterServiceStatus label={qdVehicle.tires.text.short(Localization)} status={qdVehicle.tires.hunterServiceStatusType} />
                            <div className={css.iconDescriptionBox}>
                                <div className={css.imageWrapper}>
                                    <ImageHelper
                                        imageProps={{
                                            src: qdVehicle.tires.iconImg,
                                            alt: qdVehicle.tires.recommended
                                                ? Localization.TireTreadFailedInspectionIcon
                                                : Localization.TireTreadPassedInspectionIcon,
                                        }}
                                    />
                                </div>
                                <h5 className={css.description}>{qdVehicle.tires.text.long(Localization)}</h5>
                            </div>
                            <div className={css.graphicWrapper}>
                                <HunterTireCardGrid>
                                    <HunterTireCard
                                        imageHelper={
                                            <ImageHelper
                                                imageProps={{
                                                    src: qdVehicle.tireData.front.left.tread?.image,
                                                    alt: Localization.formatString(Localization.HunterTireTreadImage, Localization.DriverFront) as string,
                                                }}
                                                fullyQualified
                                            />
                                        }
                                        label={Localization.DriverFront}
                                        tread={qdVehicle.tireData.front.left.tread?.value || 'N/A'}
                                        status={qdVehicle.tireData.front.left.tread?.status}
                                    />
                                    <HunterTireCard
                                        imageHelper={
                                            <ImageHelper
                                                imageProps={{
                                                    src: qdVehicle.tireData.front.right.tread?.image,
                                                    alt: Localization.formatString(Localization.HunterTireTreadImage, Localization.PassengerFront) as string,
                                                }}
                                                fullyQualified
                                            />
                                        }
                                        label={Localization.PassengerFront}
                                        tread={qdVehicle.tireData.front.right.tread?.value || 'N/A'}
                                        status={qdVehicle.tireData.front.right.tread?.status}
                                    />
                                    <HunterTireCard
                                        imageHelper={
                                            <ImageHelper
                                                imageProps={{
                                                    src: qdVehicle.tireData.rear.left.tread?.image,
                                                    alt: Localization.formatString(Localization.HunterTireTreadImage, Localization.DriverRear) as string,
                                                }}
                                                fullyQualified
                                            />
                                        }
                                        label={Localization.DriverRear}
                                        tread={qdVehicle.tireData.rear.left.tread?.value || 'N/A'}
                                        status={qdVehicle.tireData.rear.left.tread?.status}
                                    />
                                    <HunterTireCard
                                        imageHelper={
                                            <ImageHelper
                                                imageProps={{
                                                    src: qdVehicle.tireData.rear.right.tread?.image,
                                                    alt: Localization.formatString(Localization.HunterTireTreadImage, Localization.PassengerRear) as string,
                                                }}
                                                fullyQualified
                                            />
                                        }
                                        label={Localization.PassengerRear}
                                        tread={qdVehicle.tireData.rear.right.tread?.value || 'N/A'}
                                        status={qdVehicle.tireData.rear.right.tread?.status}
                                    />
                                </HunterTireCardGrid>
                            </div>
                            {treadService &&
                                treadLocalized &&
                                qdVehicle.tires.hunterServiceStatusType !== HunterServiceStatusType.OK &&
                                qdVehicle.tires.hunterServiceStatusType !== HunterServiceStatusType.NoData ?
                                (
                                    <GMUpsellButton
                                        title={treadSelected ? undefined : Localization.ClickToAddThisService}
                                        label={treadSelected
                                            ? Localization.formatString(Localization.RemoveGenericFormat, Localization.TireReview)
                                                .toString()
                                            : Localization.formatString(Localization.AddGenericFormat, Localization.TireReview)
                                                .toString()}
                                        styleType={treadSelected ? 'primary' : 'secondary'}
                                        onClick={() => {
                                            treadService && cartDispatch({
                                                type: Types.ToggleService,
                                                payload: treadService,
                                            });
                                        }}
                                        add={!treadSelected}
                                        id={treadSelected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                                        ariaLabel={treadSelected ? Localization.ClickToRemoveService : Localization.ClickToAddThisService}
                                    />
                                ) : null}
                        </div>
                    )}
                </div>
            </div>
        </ScrollableBody>
    ) : (
        <Spinner />
    );
});
